'use client';

import { usePathname, useRouter } from 'next/navigation';
import { SelectedPage } from '@/types/types';
import { useAppSelector, useAppDispatch } from '@/hooks/reduxHooks';
import { setSelectedPage } from '@/features/menuLinks/menuLinksSlice';
import { scroller } from 'react-scroll';

type Props = {
  page: string;
  offset?: number;
  isSubItem?: boolean;
  targetId?: string;
  onNavigate?: () => void;
};

export const AnchorMenuLink = ({
  page,
  offset = -40,
  isSubItem = false,
  targetId,
  onNavigate,
}: Props) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const theme = useAppSelector((state) => state.theme.theme);
  const selectedPage = useAppSelector((state) => state.menuLinks.selectedPage);

  const themedText = theme === 'light' ? 'text-secondary-600' : 'text-gray-50';
  const anchorId = (targetId ?? page.toLowerCase().replace(/ /g, '')) as SelectedPage;

  const handleClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    dispatch(setSelectedPage(anchorId));

    const scrollToSection = () =>
      scroller.scrollTo(anchorId, {
        duration: 800,
        delay: 0,
        smooth: 'easeInOutQuart',
        offset,
      });

    if (pathname !== '/') {
      router.push(`/#${anchorId}`);
      onNavigate?.();
      return;
    }

    scrollToSection();
    router.push(`#${anchorId}`);
    onNavigate?.();
  };

  const baseTextSize = isSubItem ? 'text-sm' : 'text-base';
  const indentation = isSubItem ? 'pl-4' : '';

  const activeClass =
    selectedPage === anchorId ? 'text-primary-500' : themedText;

  return (
    <span
      className={`${activeClass} ${baseTextSize} ${indentation} cursor-pointer transition duration-500 hover:text-primary-400/90 dark:hover:text-primary-200`}
      onClick={handleClick}
    >
      {page}
    </span>
  );
};
